import { useState } from 'react';
import { fetchGetStateByStateKey } from '@iwoca/lapi-client/edge';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { TApplication } from 'lendingApi/applications.types';
import { TGetStateResponseData } from 'lendingApi/lapiOverrides.types';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useContinueRedirect } from '../useContinueRedrect.hook';

const getBaseURL = (isDE: boolean) =>
  `${window.location.protocol}//${window.location.hostname}${
    window.location.port ? ':' + window.location.port : ''
  }${getEnvironment() !== 'development' ? (isDE ? '/partner/portal' : '/introducer/portal') : ''}`;

export const useOpenInNewTab = (application: TApplication) => {
  const { isDE, isUK } = useLocale();
  const { trackEvent } = useAmplitude();
  const { getRedirectUrl } = useContinueRedirect();
  const [isLoading, setIsLoading] = useState(false);
  const baseURL = getBaseURL(isDE);
  const openInNewTab = (path: string) =>
    window.open(`${baseURL}${path}`, '_blank');

  const openApplicationInLatestStep = async () => {
    if (!application.customer.state_key) return;

    setIsLoading(true);

    try {
      const response = await fetchGetStateByStateKey({
        stateKey: application.customer.state_key,
      });
      const state = response.data as TGetStateResponseData;
      openInNewTab(
        getRedirectUrl(
          application.application_uid,
          state.ui?.tip_latest_signup_step,
        ),
      );
    } catch {
      openInNewTab(getRedirectUrl(application.application_uid));
    } finally {
      setIsLoading(false);
    }
  };

  const getApplicationTarget = (application: TApplication) => {
    if (application.is_partner_channel)
      return PATH_NAMES.application({
        applicationId: application.application_uid,
      });

    if (
      application.status === APPLICATION_STATUS.action_required ||
      (isUK && application.status === APPLICATION_STATUS.manual_review)
    )
      return PATH_NAMES.applicationDocuments({
        applicationId: application.application_uid,
      });

    return PATH_NAMES.application({
      applicationId: application.application_uid,
    });
  };

  const handleOpenNewTabClick = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    trackEvent(AMPLITUDE_EVENTS.openApplicationInNewTabClicked, {
      'Application status': application.status,
    });

    if (application.status === 'open') {
      return await openApplicationInLatestStep();
    }

    const target = getApplicationTarget(application);

    openInNewTab(target);
  };

  return { isDE, isLoading, handleOpenNewTabClick };
};
